import { useState, useEffect } from "react";
import { templatesApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  FileText, 
  Plus, 
  Trash2, 
  Printer, 
  Eye, 
  Edit,
  Mail,
  Cloud,
  Loader2,
  History,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp
} from "lucide-react";

export default function FirmTemplates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [expandedForms, setExpandedForms] = useState({});
  const [customFormName, setCustomFormName] = useState("");
  const [editingLetter, setEditingLetter] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    template_type: "",
    filing_method: "efiled",
    description: "",
    include_irs_disclaimer: false,
  });
  
  const [letterData, setLetterData] = useState({
    client_name: "",
    letter_date: "",
    forms: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [t, d, l] = await Promise.all([
        templatesApi.getAll(),
        templatesApi.getDefaults(),
        templatesApi.getLetters()
      ]);
      setTemplates(t.data);
      setDefaults(d.data);
      setGeneratedLetters(l.data);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newTemplate.name || !newTemplate.template_type) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const forms = defaults?.default_forms?.[newTemplate.template_type] || [];
      await templatesApi.create({ ...newTemplate, forms });
      toast.success("Template created");
      setShowCreateDialog(false);
      setNewTemplate({ name: "", template_type: "", filing_method: "efiled", description: "", include_irs_disclaimer: false });
      loadData();
    } catch (e) {
      toast.error("Failed to create");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await templatesApi.delete(id);
      toast.success("Deleted");
      loadData();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const openGenerate = (t) => {
    setSelectedTemplate(t);
    const forms = (t.forms || []).map(f => ({ ...f, include: f.include !== false }));
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    setLetterData({ client_name: "", letter_date: today, forms });
    // Expand forms that are included
    const expanded = {};
    forms.forEach((f, i) => { if (f.include) expanded[i] = true; });
    setExpandedForms(expanded);
    setShowGenerateDialog(true);
  };

  const handleGenerate = async () => {
    if (!letterData.client_name) {
      toast.error("Enter client name");
      return;
    }
    try {
      const r = await templatesApi.generateLetter({
        template_id: selectedTemplate.id,
        ...letterData
      });
      setPreviewHtml(r.data.html_content);
      setPreviewTitle(r.data.template_name + " - " + letterData.client_name);
      setShowGenerateDialog(false);
      setShowPreview(true);
      loadData();
      toast.success("Generated");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(previewHtml);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  const viewLetter = async (l) => {
    try {
      const r = await templatesApi.getLetter(l.id);
      setPreviewHtml(r.data.html_content);
      setPreviewTitle(r.data.template_name + " - " + r.data.client_name);
      setShowPreview(true);
    } catch (e) {
      toast.error("Failed");
    }
  };

  const addCustomForm = () => {
    if (!customFormName.trim()) {
      toast.error("Enter a form name");
      return;
    }
    const newForm = {
      form_name: customFormName.trim(),
      form_code: "CUSTOM_" + Date.now(),
      include: true,
      filing_method: "efiled",
      filing_description: "",
      payment_due: false,
      payment_amount: null,
      payment_draft_date: null,
      payment_method: "",
      mail_deadline: null,
      special_instructions: [],
      has_recipient_copies: false,
      recipient_distribution: "",
      mail_distribution: ""
    };
    const newForms = [...letterData.forms, newForm];
    setLetterData({ ...letterData, forms: newForms });
    setExpandedForms({ ...expandedForms, [newForms.length - 1]: true });
    setCustomFormName("");
    toast.success("Form added");
  };

  const removeForm = (idx) => {
    const newForms = letterData.forms.filter((_, i) => i !== idx);
    setLetterData({ ...letterData, forms: newForms });
  };

  const updateForm = (idx, field, val) => {
    const forms = letterData.forms.map((f, i) => i === idx ? { ...f, [field]: val } : f);
    setLetterData({ ...letterData, forms });
  };

  const openEditDialog = async (letter) => {
    try {
      // Load full letter data
      const r = await templatesApi.getLetter(letter.id);
      setEditingLetter(r.data);
      setShowEditDialog(true);
    } catch (e) {
      toast.error("Failed to load letter");
    }
  };

  const handleSaveEdit = async () => {
    if (!editingLetter) return;
    
    setEditLoading(true);
    try {
      const r = await templatesApi.updateLetter(editingLetter.id, {
        client_name: editingLetter.client_name,
        letter_date: editingLetter.letter_date,
        forms: editingLetter.forms
      });
      
      // Update preview with new HTML
      setPreviewHtml(r.data.html_content);
      setPreviewTitle(editingLetter.template_name + " - " + editingLetter.client_name);
      
      // Refresh letters list
      loadData();
      setShowEditDialog(false);
      setShowPreview(true);
      toast.success("Letter updated successfully");
    } catch (e) {
      toast.error("Failed to update letter");
    } finally {
      setEditLoading(false);
    }
  };

  const updateEditForm = (idx, field, val) => {
    const forms = editingLetter.forms.map((f, i) => i === idx ? { ...f, [field]: val } : f);
    setEditingLetter({ ...editingLetter, forms });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="firm-templates-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Firm Templates</h1>
          <p className="text-muted-foreground">Create client instruction letters</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> New Template
          </Button>
        )}
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates"><FileText className="h-4 w-4 mr-2" />Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="history"><History className="h-4 w-4 mr-2" />Letters ({generatedLetters.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4">
          {templates.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No templates yet</p>
              <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600">Create Template</Button>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(t => (
                <Card key={t.id} className="hover:shadow-md">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{t.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground mb-4">{t.forms?.length || 0} form section(s)</p>
                    <div className="flex gap-2">
                      <Button onClick={() => openGenerate(t)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                        <Printer className="h-4 w-4 mr-2" /> Generate
                      </Button>
                      {isAdmin && (
                        <Button variant="outline" size="icon" onClick={() => handleDelete(t.id)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {generatedLetters.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <History className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">No letters generated yet</p>
            </CardContent></Card>
          ) : (
            <Card><CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-medium text-muted-foreground">Client</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Template</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedLetters.map(l => (
                    <tr key={l.id} className="border-t hover:bg-muted">
                      <td className="p-4 font-medium">{l.client_name}</td>
                      <td className="p-4 text-muted-foreground">{l.template_name}</td>
                      <td className="p-4 text-muted-foreground">{l.letter_date}</td>
                      <td className="p-4 text-right space-x-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(l)}>
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => viewLetter(l)}>
                          <Eye className="h-4 w-4 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Template</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={newTemplate.name} onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })} placeholder="Template name" />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={newTemplate.template_type} onValueChange={v => setNewTemplate({ ...newTemplate, template_type: v })}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {defaults?.template_types?.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Filing Method</Label>
              <Select value={newTemplate.filing_method} onValueChange={v => setNewTemplate({ ...newTemplate, filing_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {defaults?.filing_methods?.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={newTemplate.description} onChange={e => setNewTemplate({ ...newTemplate, description: e.target.value })} rows={2} />
            </div>
            <div className="flex justify-between items-center">
              <Label>IRS Disclaimer</Label>
              <Switch checked={newTemplate.include_irs_disclaimer} onCheckedChange={c => setNewTemplate({ ...newTemplate, include_irs_disclaimer: c })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-blue-600">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Letter</DialogTitle>
            <p className="text-sm text-muted-foreground">{selectedTemplate?.name}</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input value={letterData.client_name} onChange={e => setLetterData({ ...letterData, client_name: e.target.value })} placeholder="Enter client name" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input value={letterData.letter_date} onChange={e => setLetterData({ ...letterData, letter_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-3">
              <Label className="font-semibold text-base">Form Sections</Label>
              {letterData.forms.map((f, i) => (
                <Card key={i} className={!f.include ? 'opacity-60' : ''}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <Switch checked={f.include} onCheckedChange={c => { updateForm(i, 'include', c); setExpandedForms({...expandedForms, [i]: c}); }} />
                      <span className="text-sm font-medium flex-1">{f.form_name}</span>
                      {f.form_code?.startsWith('CUSTOM_') && (
                        <Button variant="ghost" size="sm" onClick={() => removeForm(i)} className="text-red-500 hover:text-red-700 h-6 w-6 p-0">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setExpandedForms({...expandedForms, [i]: !expandedForms[i]})}>
                        {expandedForms[i] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                    {expandedForms[i] && f.include && (
                      <div className="mt-3 pt-3 border-t space-y-3">
                        {/* Personal Property Schedule - Mailing Distribution */}
                        {f.form_code === 'PP_SCHEDULE' && (
                          <div className="space-y-2 p-3 bg-amber-500/10 rounded-lg">
                            <Label className="text-xs font-medium text-amber-800">Mailing</Label>
                            <Select value={f.mail_distribution || 'client_mails'} onValueChange={v => updateForm(i, 'mail_distribution', v)}>
                              <SelectTrigger className="h-8 bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="firm_mailed">We have mailed the form</SelectItem>
                                <SelectItem value="client_mails">Client needs to mail</SelectItem>
                              </SelectContent>
                            </Select>
                            {f.mail_distribution !== 'firm_mailed' && (
                              <div className="space-y-1 mt-2">
                                <Label className="text-xs">Mail Deadline</Label>
                                <Input value={f.mail_deadline || ''} onChange={e => updateForm(i, 'mail_deadline', e.target.value)} placeholder="e.g., Feb 2nd" className="h-8" />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Sales Tax, Business Tax, Liquor Tax - E-filed with payment drafting */}
                        {['SALES_TAX', 'BUSINESS_TAX', 'LIQUOR_TAX'].includes(f.form_code) && (
                          <>
                            <div className="flex items-center gap-3">
                              <Switch checked={f.payment_due || false} onCheckedChange={c => updateForm(i, 'payment_due', c)} />
                              <Label className="text-xs font-medium">Payment Due</Label>
                            </div>
                            {f.payment_due && (
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Amount (optional)</Label>
                                  <div className="relative">
                                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                    <Input type="number" value={f.payment_amount || ''} onChange={e => updateForm(i, 'payment_amount', parseFloat(e.target.value) || '')} placeholder="0.00" className="h-8 pl-7" />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Draft Date *</Label>
                                  <div className="relative">
                                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                    <Input value={f.payment_draft_date || ''} onChange={e => updateForm(i, 'payment_draft_date', e.target.value)} placeholder="01.30.2026" className="h-8 pl-7" />
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Annual Report - E-filed with payment method choice */}
                        {f.form_code === 'ANNUAL_REPORT' && (
                          <>
                            <div className="flex items-center gap-3">
                              <Switch checked={f.payment_due || false} onCheckedChange={c => updateForm(i, 'payment_due', c)} />
                              <Label className="text-xs font-medium">Payment Due</Label>
                            </div>
                            {f.payment_due && (
                              <div className="space-y-3">
                                <div className="space-y-2 p-3 bg-green-500/10 rounded-lg">
                                  <Label className="text-xs font-medium text-green-800">Payment Method</Label>
                                  <Select value={f.payment_method || 'we_draft'} onValueChange={v => updateForm(i, 'payment_method', v)}>
                                    <SelectTrigger className="h-8 bg-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="we_draft">We will draft payment</SelectItem>
                                      <SelectItem value="client_mails_check">Client will mail a check</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Amount (optional)</Label>
                                    <div className="relative">
                                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                      <Input type="number" value={f.payment_amount || ''} onChange={e => updateForm(i, 'payment_amount', parseFloat(e.target.value) || '')} placeholder="0.00" className="h-8 pl-7" />
                                    </div>
                                  </div>
                                  {f.payment_method !== 'client_mails_check' && (
                                    <div className="space-y-1">
                                      <Label className="text-xs">Draft Date</Label>
                                      <div className="relative">
                                        <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                        <Input value={f.payment_draft_date || ''} onChange={e => updateForm(i, 'payment_draft_date', e.target.value)} placeholder="01.30.2026" className="h-8 pl-7" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Recipient Distribution for 1099, W2/W3 */}
                        {f.has_recipient_copies && (
                          <div className="space-y-2 p-3 bg-blue-500/10 rounded-lg">
                            <Label className="text-xs font-medium text-blue-800">Recipient Copies Distribution</Label>
                            <Select value={f.recipient_distribution || 'client_distributes'} onValueChange={v => updateForm(i, 'recipient_distribution', v)}>
                              <SelectTrigger className="h-8 bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="firm_mailed">{f.form_code === 'W2W3' ? "We have mailed employees' copies" : "We have mailed recipient copies"}</SelectItem>
                                <SelectItem value="client_distributes">{f.form_code === 'W2W3' ? "Client will distribute to employees" : "Client will distribute to recipients"}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {/* Standard payment options for other forms (941, 940, SUTA, etc.) */}
                        {!['PP_SCHEDULE', 'SALES_TAX', 'BUSINESS_TAX', 'LIQUOR_TAX', 'ANNUAL_REPORT'].includes(f.form_code) && !f.has_recipient_copies && (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Filing Method</Label>
                                <Select value={f.filing_method} onValueChange={v => updateForm(i, 'filing_method', v)}>
                                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="efiled">E-Filed</SelectItem>
                                    <SelectItem value="mailed">Mailed</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {f.filing_method === 'mailed' && (
                                <div className="space-y-1">
                                  <Label className="text-xs">Mail Deadline</Label>
                                  <Input value={f.mail_deadline || ''} onChange={e => updateForm(i, 'mail_deadline', e.target.value)} placeholder="e.g., Feb 2nd" className="h-8" />
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <Switch checked={f.payment_due || false} onCheckedChange={c => updateForm(i, 'payment_due', c)} />
                              <Label className="text-xs font-medium">Payment Due</Label>
                            </div>
                            {f.payment_due && (
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Amount</Label>
                                  <div className="relative">
                                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                    <Input type="number" value={f.payment_amount || ''} onChange={e => updateForm(i, 'payment_amount', parseFloat(e.target.value) || '')} placeholder="0.00" className="h-8 pl-7" />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Draft Date</Label>
                                  <div className="relative">
                                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                    <Input value={f.payment_draft_date || ''} onChange={e => updateForm(i, 'payment_draft_date', e.target.value)} placeholder="01.30.2026" className="h-8 pl-7" />
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* For 1099/W2W3, show standard payment options too */}
                        {f.has_recipient_copies && (
                          <>
                            <div className="flex items-center gap-3">
                              <Switch checked={f.payment_due || false} onCheckedChange={c => updateForm(i, 'payment_due', c)} />
                              <Label className="text-xs font-medium">Payment Due</Label>
                            </div>
                            {f.payment_due && (
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Amount</Label>
                                  <div className="relative">
                                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                    <Input type="number" value={f.payment_amount || ''} onChange={e => updateForm(i, 'payment_amount', parseFloat(e.target.value) || '')} placeholder="0.00" className="h-8 pl-7" />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Draft Date</Label>
                                  <div className="relative">
                                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                    <Input value={f.payment_draft_date || ''} onChange={e => updateForm(i, 'payment_draft_date', e.target.value)} placeholder="01.30.2026" className="h-8 pl-7" />
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              {/* Custom Form Input */}
              <div className="flex gap-2 mt-4 p-3 bg-muted rounded-lg">
                <Input 
                  value={customFormName} 
                  onChange={e => setCustomFormName(e.target.value)} 
                  placeholder="Add custom form (e.g., Franchise Tax)" 
                  className="flex-1"
                  onKeyDown={e => e.key === 'Enter' && addCustomForm()}
                />
                <Button onClick={addCustomForm} variant="outline" className="shrink-0">
                  <Plus className="h-4 w-4 mr-1" /> Add Form
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerate} className="bg-emerald-600"><Printer className="h-4 w-4 mr-2" />Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{previewTitle}</DialogTitle></DialogHeader>
          <div className="border rounded-lg bg-white">
            <iframe srcDoc={previewHtml} className="w-full h-[55vh] border-0" title="Preview" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
            <Button onClick={handlePrint} className="bg-blue-600"><Printer className="h-4 w-4 mr-2" />Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Letter Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Letter</DialogTitle>
          </DialogHeader>
          {editingLetter && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name</Label>
                  <Input 
                    value={editingLetter.client_name} 
                    onChange={e => setEditingLetter({ ...editingLetter, client_name: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Letter Date</Label>
                  <Input 
                    value={editingLetter.letter_date} 
                    onChange={e => setEditingLetter({ ...editingLetter, letter_date: e.target.value })} 
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-base font-semibold">Forms</Label>
                <div className="space-y-3">
                  {editingLetter.forms?.filter(f => f.include !== false).map((form, idx) => (
                    <div key={idx} className="p-3 bg-muted rounded-lg space-y-3">
                      <div className="font-medium text-foreground">{form.form_name}</div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Filing Method</Label>
                          <Select value={form.filing_method} onValueChange={v => updateEditForm(idx, "filing_method", v)}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="efiled">E-Filed</SelectItem>
                              <SelectItem value="mailed">Mailed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-4">
                          <Switch 
                            checked={form.payment_due} 
                            onCheckedChange={v => updateEditForm(idx, "payment_due", v)} 
                          />
                          <Label className="text-xs">Payment Due</Label>
                        </div>
                      </div>
                      
                      {form.payment_due && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Amount</Label>
                            <Input 
                              type="number" 
                              className="h-8"
                              value={form.payment_amount || ""} 
                              onChange={e => updateEditForm(idx, "payment_amount", parseFloat(e.target.value) || null)} 
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Draft Date</Label>
                            <Input 
                              className="h-8"
                              value={form.payment_draft_date || ""} 
                              onChange={e => updateEditForm(idx, "payment_draft_date", e.target.value)} 
                              placeholder="MM.DD.YYYY"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={editLoading} className="bg-emerald-600">
              {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
